import React from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";

const LoanViewRow = ({ details, monthlyCostDetailData, doughnutData, downPaymentAmount }) => {

    const formatCurrency = (value) => {
      if (isNaN(value)) return value;
      return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
        .format(value)
        .replace("kr", "")
        .trim();
    };

    // Calculate loan and down payment information
    const propertyPrice = details.utgangspris || 0;
    const downPayment = downPaymentAmount || 0;
    const loanAmount = propertyPrice - downPayment;
    const ltv = propertyPrice > 0 ? ((loanAmount / propertyPrice) * 100) : 0;
    const downPaymentPercentage = propertyPrice > 0 ? ((downPayment / propertyPrice) * 100) : 0;

    return (
        <Card className="stat-card border-0" style={{ borderRadius: '12px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
                <h6 style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6c757d',
                    marginBottom: '0.5rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                }}>
                    Finansiering
                </h6>
                <h2 style={{
                    fontSize: '3.5rem',
                    fontWeight: '700',
                    color: '#003D7A',
                    marginBottom: '0.75rem',
                    lineHeight: '1.1',
                }}>
                    {formatCurrency(propertyPrice)} kr
                </h2>

                <div style={{
                    marginBottom: '1rem',
                    marginLeft: '-1.5rem',
                    marginRight: '-1.5rem',
                    paddingLeft: '1.5rem',
                    paddingRight: '1.5rem',
                }}>
                    {/* Stacked Progress Bar */}
                    <div style={{
                        display: 'flex',
                        height: '40px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: '#F0F2F5',
                        marginBottom: '0.75rem',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                    }}>
                        {/* Loan Amount */}
                        <div
                            style={{
                                width: `${ltv}%`,
                                backgroundColor: '#003D7A',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                transition: 'width 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRight: ltv > 0 && ltv < 100 ? '2px solid rgba(255,255,255,0.3)' : 'none',
                                padding: '0 4px',
                            }}
                            title={`Lånebelopp: ${formatCurrency(loanAmount)} kr`}
                        >
                            {ltv > 10 && (
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                    {formatCurrency(loanAmount)} kr
                                </span>
                            )}
                        </div>

                        {/* Down Payment */}
                        <div
                            style={{
                                width: `${downPaymentPercentage}%`,
                                backgroundColor: '#4A90E2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                transition: 'width 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden',
                                padding: '0 4px',
                            }}
                            title={`Egeninsats: ${formatCurrency(downPayment)} kr`}
                        >
                            {downPaymentPercentage > 10 && (
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                    {formatCurrency(downPayment)} kr
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Legend */}
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    backgroundColor: '#003D7A',
                                }}
                            />
                            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                Lånebelopp (LTV {ltv.toFixed(1)}%): <strong>{formatCurrency(loanAmount)} kr</strong>
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    backgroundColor: '#4A90E2',
                                }}
                            />
                            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                Egeninsats: <strong>{formatCurrency(downPayment)} kr</strong>
                            </span>
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
}

// Requirements and export

const detailsPropTypes = PropTypes.shape({
    utgangspris: PropTypes.number.isRequired,
    driftkostnad: PropTypes.number.isRequired,
    taxeringsvarde: PropTypes.number.isRequired,
    pantbrev: PropTypes.number.isRequired,
    nyaPantbrev: PropTypes.number.isRequired,
    kostnadNyaPantbrev: PropTypes.number.isRequired,
    kostnadLagfart: PropTypes.number.isRequired,
});

const monthlyCostDetailDataPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })
);

const doughnutDataPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired, 
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  );

LoanViewRow.propTypes = {
    details: detailsPropTypes,
    monthlyCostDetailData: monthlyCostDetailDataPropTypes,
    doughnutData: doughnutDataPropTypes,
    downPaymentAmount: PropTypes.number,
};

LoanViewRow.defaultProps = {
    downPaymentAmount: 0,
};

export default LoanViewRow;